import * as util from 'util';
import {Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {GeneralService} from "../../services/generalService";
import {RouteService} from "../../services/routeService";
import {NotificationService} from "../../services/notificationService";
import {DICTIONARY} from "../../dictionary";
import {
	NotificationOptionsComponent
} from "../admin/inbound/inboundShield/threatPrevention/notification-options/notification-options.component";
import {LookAndFeelComponent} from "../admin/outbound/lookAndFeel/look-and-feel.component";
import {OnboardingMailboxesComponent} from "./onboarding-mailboxes/onboarding-mailboxes.component";
import {OnboardingCopyPolicyComponent} from "./onboarding-copy-policy/onboarding-copy-policy.component";
import {buildInboundPreviewSpec, buildOutboundPreviewSpec} from '../admin/adminCenter/emailFlow/email-flow-specs';

@Component({
	selector: 'onboarding-wizard-component',
	templateUrl: './onboarding-wizard.component.html',
	styleUrls: ['./onboarding-wizard.component.scss'],
})
export class OnboardingWizardComponent implements OnInit, OnDestroy {
	@Output() closeCb = new EventEmitter<any>();
	@ViewChild(NotificationOptionsComponent) notificationOptionsComponent: NotificationOptionsComponent;
	@ViewChild(LookAndFeelComponent) lookAndFeelComponent: LookAndFeelComponent;
	@ViewChild(OnboardingMailboxesComponent) onboardingMailboxesComponent: OnboardingMailboxesComponent;
	@ViewChild(OnboardingCopyPolicyComponent) onboardingCopyPolicyComponent: OnboardingCopyPolicyComponent;

	constructor(public gs: GeneralService, private rs: RouteService, private ns: NotificationService) {}

	dic = DICTIONARY;

	outboundSelected = true;
	inboundSelected = true;
	confirmedOutboundArch: string;
	confirmedInboundArch: string;
	updatingArchitecture: boolean;
	inboundGoogleIps: {allowlist: string, gateway: string, regex: string};

	isPsAvailable;

	steps = {
		environmentScope: {
			num: 1,
			label: 'Environment & Scope',
			skippable: false,
			visible: true,
			subSteps: {
				selectEnvironmentAndScope: { label: 'Select environment & scope', title: '', explanation: '' },
			},
			emailServices: {
				exchangeCloud: {
					key: this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud,
					label: 'Microsoft 365',
					subtitle: 'Exchange online',
					logo: 'images/integrationsLogos/exchange-logo.png',
					size: 'large',
				},
				onPremises: {
					key: this.dic.CONSTANTS.onboardingFormValues.currentEnv.onPermises,
					label: 'Microsoft Exchange',
					subtitle: 'On-Premises (only)',
					logo: 'images/integrationsLogos/exchange-logo.png',
					size: 'small',
				},
				hybrid: {
					key: this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid,
					label: 'Microsoft Exchange',
					subtitle: 'Hybrid',
					logo: 'images/integrationsLogos/exchange-logo.png',
					size: 'small',
				},
				gSuite: {
					key: this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite,
					label: 'Google Workspace',
					subtitle: 'Gmail',
					logo: 'images/integrationsLogos/gsuite-logo.png',
					size: 'large',
				},
				other: {
					key: this.dic.CONSTANTS.onboardingFormValues.currentEnv.other,
					label: 'Other SMTP-based',
					subtitle: 'mail system',
					logo: null,
					size: 'small',
				},
			},
			selectedEmailService: this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud,
			protectionScopes: {
				both: {
					key: 'both',
					label: 'Full protection',
					subtitle: 'Inbound & Outbound',
					recommended: true,
					size: 'large',
				},
				inbound: {
					key: this.dic.CONSTANTS.onboardingSetupScopes.inbound,
					label: 'Inbound emails only',
					subtitle: 'Threat protection',
					recommended: false,
					size: 'small',
				},
				outbound: {
					key: this.dic.CONSTANTS.onboardingSetupScopes.outbound,
					label: 'Outbound emails only',
					subtitle: 'Encryption &\nData Loss Prevention',
					recommended: false,
					size: 'small',
				},
			},
			selectedScope: 'both',
		},
		emailArchitecture: {
			num: 2,
			label: 'Email Architecture',
			skippable: false,
			visible: true,
			subSteps: {
				outboundArch: { label: 'Outbound architecture', title: '', explanation: 'Select how outbound emails will be routed through Trustifi for encryption, DLP, and security policy enforcement.', visible: true, guide: 'https://docs.trustifi.com/docs/outbound-integration' },
				inboundArch:  { label: 'Inbound architecture',  title: '',  explanation: 'Select how inbound emails will be routed through Trustifi for threat detection, anti-phishing, and spam protection.', visible: true, guide: 'https://docs.trustifi.com/docs/inbound-integration' },
			},
		},
		mailServer: {
			num: 3,
			label: 'API Integration',
			skippable: true,
			visible: true,
			subSteps: {
				connectEmailIntegration: {label: 'Connect your environment', title: 'Connect Trustifi to your environment', explanation: 'Complete the connection to enable contact sync and additional security features.'}
			},
		},
		generalSettings: {
			num: 3,
			label: 'General Settings',
			skippable: true,
			visible: true,
			subSteps: {
				settings:   { label: 'General Settings',        title: '',       explanation: 'Set your preferred region to control where your secure email data is stored. This affects new data only and will not impact existing records.', guide: 'https://docs.trustifi.com/docs/secure-data-storage#choosing-your-data-storage-region' },
				branding:   { label: 'Branding (Look & Feel)', title: '', explanation: 'Customize your encrypted email template with your organization\'s logo and brand colors to create a consistent and professional experience for recipients.' },
				copyPolicy: { label: 'Copy Policy',            title: 'Copy Policy from Another Admin',   explanation: 'Copy security policies from an existing admin to quickly apply a proven configuration to this plan.', visible: !!this.gs.showCopyPolicyBtn },
			},
		},
		configureDomains: {
			num: 4,
			label: 'Domain Verification',
			skippable: true,
			visible: true,
			subSteps: {
				domains: { label: 'Domain Verification', title: 'Which domains do you want to protect with Trustifi?', explanation: 'Verify your organization\'s sending domains to ensure proper email delivery and compliance with SPF and DKIM policies.', guide: 'https://docs.trustifi.com/docs/domain-verification-process' },
			},
		},
		outboundShield: {
			num: 5,
			label: 'Outbound Rules & Policies',
			skippable: true,
			visible: true,
			subSteps: {
				rules:    { label: 'Outbound rules',     title: 'Configure Outbound Rules',     explanation: 'Define outbound rules to automatically apply encryption, DLP policies, and other security actions to outgoing emails based on conditions you configure.', guide: 'https://docs.trustifi.com/docs/dlp-rules' },
				policies: { label: 'Outbound policies',  title: 'Configure Outbound Policies',  explanation: 'Configure outbound policies to enforce organization-wide settings for outgoing emails, including authentication methods, content restrictions, and retention.', guide: 'https://docs.trustifi.com/docs/setting-policies' },
			},
		},
		deployOutboundShield: {
			num: 6,
			label: 'Outbound Deployment',
			skippable: true,
			visible: true,
			subSteps: {
				verifyMta: { label: 'Verify MTA', title: '', explanation: 'Verify that your MTA endpoints are reachable and correctly configured before sending outbound emails.', visible: false },
				deploy: { label: 'Deploy Outbound Shield', title: 'Deploy Outbound Shield', explanation: '' },
				verify: { label: 'Verify', title: '', explanation: '' },
			},
		},
		inboundShield: {
			num: 7,
			label: 'Inbound Settings',
			skippable: true,
			visible: true,
			subSteps: {
				threatPreventionRules: { label: 'Threat Prevention Rules', title: '', explanation: 'Configure how different threat types are handled - set which emails get quarantined, who gets notified, and what actions are taken automatically.', guide: 'https://docs.trustifi.com/docs/threat-prevention-rules' },
				smartBanner: { label: 'Smart Banners', title: '', explanation: '' },
				reviewers: { label: 'Reviewers', title: '', explanation: 'Add the reviewers who will handle quarantined emails, and set the permissions each of them has.' },
				notificationOptions: { label: 'Notification', title: '', explanation: 'Configure when reviewers and recipients should receive email notifications for received threats.' },
				additionalSettings: { label: 'Additional Settings', title: '', explanation: 'Additional Inbound Shield settings.' },
			},
		},
		deployInboundShield: {
			num: 8,
			label: 'Inbound Deployment',
			skippable: true,
			visible: true,
			subSteps: {
				verifyMta: { label: 'Verify MTA', title: '', explanation: 'Verify that your MTA endpoints are reachable and correctly configured before deploying inbound protection.', visible: false },
				mailboxes: { label: 'Protected Mailboxes', title: 'Selective Mailbox Protection (optional)', explanation: 'By default, every mailbox under your domains is protected once your MX records point to Trustifi, and each protected mailbox uses an Inbound Shield license. To protect only specific mailboxes, list them here - emails to unlisted mailboxes are delivered to your mail server without scanning. Set this up before changing your MX records.', visible: false },
				deploy: { label: 'Deploy Inbound Shield', title: 'Deploy Inbound Shield', explanation: '' },
				verify: { label: 'Verify', title: '', explanation: '' },
			},
		},
	};

	activeStepKey = 'environmentScope';
	activeSubStepKey = 'selectEnvironmentAndScope';
	isFirstPosition = true;
	hasNextPosition = true;
	wizardComplete = false;
	wizardError = '';
	noScopePermission = false;
	userInfo: any;
	timeZones = this.gs.getAllTimeZones();
	selectedTimeZone;
	selectedOutboundArchitecture: any;
	emailRelayHeader = 'x-trustifi-creds';
	showEmailRelayKey;
	showEmailRelayUsername;
	showEmailRelayPassword;
	regenerateInProcessER = false;
	showJournalAddress;
	showInboundJournalAddress;
	showInboundRelayKey;
	showInboundRelayUsername;
	showInboundRelayPassword;
	inboundEmailRelayHeader = 'x-trustifi-creds';
	updateMtaInProcess = false;
	outboundRelayDomainErr = false;
	mtaToMove: any;
	mtaInEdit: any;
	updateInboundMtaInProcess = false;
	inboundRelayDomainErr = false;
	inboundMtaToMove: any;
	inboundMtaInEdit: any;
	outboundDeployMode;
	inboundDeployMode;
	verifyPollingInterval: any;
	verifyTraceResults: any[];
	verifyTraceInProcess = false;
	mainRulePreset;
	updateUrlRewriteSettingsInProcess;

	outboundArchitectures = {
		[this.dic.CONSTANTS.sendingArchitecture.trustifi]: {
			title: 'Trustifi MTA Architecture',
			shortName: 'Trustifi MTA',
			icon: 'far fa-project-diagram',
			description: 'Outbound mail leaves your environment through a send connector to Trustifi\'s secure SMTP relay, where encryption and DLP policies are applied. Trustifi\'s own MTA - powered by Amazon SES - then delivers the message to the recipient.',
			whyThisFits: 'The simplest setup and fastest outbound mail flow, with routing rules that support a limited-scope POC.',
			mailFlow: [
				'Your mail system routes outbound mail to Trustifi via a send connector',
				'Trustifi applies encryption, DLP rules and other outbound policies',
				'Trustifi\'s MTA (Amazon SES) delivers the message',
				'The message reaches the external recipient',
			],
		},
		[this.dic.CONSTANTS.sendingArchitecture.client]: {
			title: 'Your MTA Architecture',
			shortName: 'Your MTA',
			icon: 'far fa-code-branch',
			description: 'Outbound mail is sent to Trustifi for processing and then handed back to your own mail server for final delivery - so every message still leaves from your infrastructure, under your sending identity.',
			whyThisFits: 'Choose this if domain verification or DNS changes are not possible, or if the Amazon SES limitations are a concern.',
			mailFlow: [
				'Your mail system sends outbound mail to Trustifi\'s secure SMTP relay via a send connector',
				'Trustifi applies encryption, DLP rules and other outbound policies',
				'Trustifi routes the message back to your own MTA',
				'Your MTA delivers the message to the final recipient',
			],
		},
		[this.dic.CONSTANTS.sendingArchitecture.journal]: {
			title: 'Journal',
			shortName: 'Journal',
			icon: 'far fa-exchange-alt',
			description: 'Outbound mail flow is left completely untouched. Your email system sends Trustifi a copy of each outbound message for DLP scanning and analysis, giving you visibility into what your users are sending without any encryption or enforcement being applied.',
			whyThisFits: 'Visibility and DLP rule tuning only - no encryption or enforcement is applied to outbound mail.',
			mailFlow: [
				'Outbound mail is delivered to its recipient exactly as it is today - the sending path is unchanged',
				'Your email system journals a copy of each outbound message to Trustifi',
				'Trustifi scans the copy against your DLP rules and analyses the content',
				'Findings are surfaced in the Trustifi portal for review and rule tuning',
			],
		}
	};

	selectedInboundArchitecture: any;

	// CC: taken from inbound-email-relay-integration.component.ts -> sendingArchitecturesData
	inboundArchitectures = {
		[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: {
			shortName: 'Email Relay',
			icon: 'far fa-project-diagram',
			title: 'Inbound Email Relay',
			description: 'Mail flow connectors route inbound traffic to Trustifi before it reaches recipient mailboxes. Trustifi scans each message, and anything not quarantined is routed back to your mail server for delivery.',
			whyThisFits: 'Provides the strongest real-time protection and enforcement, with mail routing rules that let you limit the scope of a POC.',
			mailFlow: [
				'Inbound mail arrives at your email environment',
				'A mail flow connector routes it to Trustifi Inbound Shield',
				'Trustifi scans for phishing, spoofing, spam, impersonation and other threats',
				'Mail that is not quarantined is routed back to your email server',
				'Your email server delivers the message to the recipient\'s mailbox',
			],
		},
		[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: {
			shortName: 'MX Record',
			icon: 'far fa-at',
			title: 'MX Record',
			description: 'Your domain\'s MX record is repointed to Trustifi, so all inbound mail for the domain is scanned by Trustifi before being forwarded on to your email server.',
			whyThisFits: 'Provides domain-wide protection without connector configuration, but requires conditional routing and may be unnecessary where scoped connector-based relay is available.',
			mailFlow: [
				'Your domain\'s MX record is updated to point to Trustifi',
				'All inbound internet mail for the domain arrives at Trustifi first',
				'Trustifi scans and processes each message',
				'Mail that is not quarantined is forwarded to your email server',
				'Your email server delivers the message to the recipient\'s mailbox',
			],
		},
		[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: {
			shortName: 'API',
			icon: 'far fa-code-branch',
			title: 'API',
			description: 'Trustifi connects directly to Microsoft 365 using administrator credentials, and scans mail in the mailboxes you select - with no architectural mail flow changes at all.',
			whyThisFits: 'Best choice when no mail flow changes are permitted, or to protect a limited set of mailboxes first.',
			mailFlow: [
				'The integration is enabled from the Trustifi portal using Microsoft 365 administrator credentials',
				'Administrators select which mailboxes should be protected',
				'A message arriving in a protected mailbox is temporarily moved for scanning by Trustifi',
				'After scanning, mail that is not quarantined is returned to the inbox',
			],
		},
		[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: {
			shortName: 'Journal',
			icon: 'far fa-exchange-alt',
			title: 'Journal',
			description: 'Based on the inbound relay architecture, but live mail flow is left untouched. Your email system sends Trustifi a copy of each inbound message for scanning and analysis, while the original is delivered normally.',
			whyThisFits: 'Visibility and evaluation only - no enforcement. Threat Response can remove threats after delivery.',
			mailFlow: [
				'Inbound mail is delivered to the recipient exactly as it is today - the delivery path is unchanged',
				'Your email system journals a copy of each inbound message to Trustifi',
				'Trustifi scans the copy and reports on any threats detected',
				'If a threat is found, Threat Response can locate and remove the message from recipient mailboxes',
			],
		}
	};

	// CC: availability and recommendation per email environment (from deployment matrix)
	// Values: 'recommended', 'supported', 'conditional', or absent = not available
	outboundArchSupport = {
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: 'recommended',
			[this.dic.CONSTANTS.sendingArchitecture.client]: 'supported',
			[this.dic.CONSTANTS.sendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: 'recommended',
			[this.dic.CONSTANTS.sendingArchitecture.client]: 'supported',
			[this.dic.CONSTANTS.sendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.onPermises]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: 'recommended',
			[this.dic.CONSTANTS.sendingArchitecture.journal]: 'conditional',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: 'recommended',
			[this.dic.CONSTANTS.sendingArchitecture.client]: 'supported',
			[this.dic.CONSTANTS.sendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.other]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: 'conditional',
		},
	};

	inboundArchSupport = {
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: 'recommended',
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: 'supported',
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: 'supported',
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: 'recommended',
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: 'supported',
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: 'conditional',
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.onPermises]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: 'recommended',
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: 'conditional',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: 'recommended',
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: 'supported',
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: 'supported',
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.other]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: 'recommended',
		},
	};

	activeOutboundArchSupport: any = {};
	activeInboundArchSupport: any = {};

	updateArchSupport = () => {
		const env = this.steps?.environmentScope?.selectedEmailService;
		this.activeOutboundArchSupport = env ? (this.outboundArchSupport[env] || {}) : {};
		this.activeInboundArchSupport = env ? (this.inboundArchSupport[env] || {}) : {};
	}

	regions = this.gs.isProductionEnv() ?
		['USA-West', 'Europe-Ireland', 'Europe-Frankfurt', 'Europe-London', 'Asia-Pacific-Singapore', 'Canada-Central', 'South-America-Sao-Paulo', 'Australia-Sydney', 'UAE-Dubai'] :
		['Europe-Ireland', 'Europe-Frankfurt', 'Asia-Pacific-Singapore'];

	outboundDeploymentGuides = {
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: [
				{label: 'Microsoft 365 — Trustifi MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-for-all-o365-tenants'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.client]: [
				{label: 'Microsoft 365 — Your MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-for-all-o365-tenants-1'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365-1'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: [
				{label: 'Google Workspace — Trustifi MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-2'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.client]: [
				{label: 'Google Workspace — Your MTA Configuration', url: 'https://docs.trustifi.com/docs/your-mta-architecture'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.journal]: [
				{label: 'Google Workspace — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-google-workspace'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.onPermises]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: [
				{label: 'Exchange On-Premises — Outbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.client]: [
				{label: 'Exchange On-Premises — Outbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365-1'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: [
				{label: 'Microsoft 365 — Trustifi MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-for-all-o365-tenants'},
				{label: 'Exchange On-Premises — Outbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.client]: [
				{label: 'Microsoft 365 — Your MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-for-all-o365-tenants-1'},
				{label: 'Exchange On-Premises — Outbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365-1'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.other]: {
			[this.dic.CONSTANTS.sendingArchitecture.trustifi]: [
				{label: 'SMTP — Trustifi MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-using-trustifi-mta'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.client]: [
				{label: 'SMTP — Your MTA Configuration', url: 'https://docs.trustifi.com/docs/configuration-using-your-mta'},
			],
			[this.dic.CONSTANTS.sendingArchitecture.journal]: [],
		},
	};

	activeOutboundGuides = [];

	// CC: deployment guide URLs mapped by email environment and inbound architecture
	inboundDeploymentGuides = {
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: [
				{label: 'Microsoft 365 — Inbound Relay Configuration', url: 'https://docs.trustifi.com/docs/configuration-4'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: [
				{label: 'MX Record Configuration', url: 'https://docs.trustifi.com/docs/inbound-mx-configuration'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: [
				{label: 'Google Workspace — Inbound Relay Configuration', url: 'https://docs.trustifi.com/docs/configuration-5'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: [
				{label: 'MX Record Configuration', url: 'https://docs.trustifi.com/docs/inbound-mx-configuration'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: [
				{label: 'Google Workspace — Journaling Configuration', url: 'https://docs.trustifi.com/docs/journaling-mode-1'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.onPermises]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: [
				{label: 'Exchange On-Premises — Inbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: [
				{label: 'MX Record Configuration', url: 'https://docs.trustifi.com/docs/inbound-mx-configuration'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: [
				{label: 'Microsoft 365 — Inbound Relay Configuration', url: 'https://docs.trustifi.com/docs/configuration-4'},
				{label: 'Exchange On-Premises — Inbound Relay', url: 'https://docs.trustifi.com/docs/outbound-relay-exchange-onprem-install'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: [
				{label: 'MX Record Configuration', url: 'https://docs.trustifi.com/docs/inbound-mx-configuration'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: [
				{label: 'Microsoft 365 — Journaling Configuration', url: 'https://docs.trustifi.com/docs/configuration-in-office365'},
			],
		},
		[this.dic.CONSTANTS.onboardingFormValues.currentEnv.other]: {
			[this.dic.CONSTANTS.inboundSendingArchitecture.connector]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.mx]: [
				{label: 'MX Record Configuration', url: 'https://docs.trustifi.com/docs/inbound-mx-configuration'},
			],
			[this.dic.CONSTANTS.inboundSendingArchitecture.graph]: [],
			[this.dic.CONSTANTS.inboundSendingArchitecture.journal]: [],
		},
	};

	activeInboundGuides = [];

	isWizardLoading;
	wizardInfo: any;
	outboundPreviewSpec = null;
	inboundPreviewSpec = null;

	ngOnInit() {
		this.isWizardLoading = true;

		this.rs.getWizardInfo().then((wizardInfo) => {
			this.wizardInfo = wizardInfo;

			this.confirmedOutboundArch = wizardInfo.outbound_relay?.sending_architecture;
			this.selectOutboundArchitecture(this.confirmedOutboundArch);

			this.confirmedInboundArch = wizardInfo.inbound_relay?.sending_architecture;
			this.selectInboundArchitecture(this.confirmedInboundArch);

			// CC: auto-select a valid default scope based on permissions
			this.applyPermissionsToScope();

			this.updateStepVisibility();

			this.updateArchSupport();

			this.isWizardLoading = false;
		}, () => {
			this.isWizardLoading = false;
		});

		this.gs.getUserInfo(false, (userInfo) => {
			this.userInfo = userInfo;

			if (this.userInfo.timezone) {
				this.selectedTimeZone = this.timeZones.find(timezoneItem => timezoneItem.zone === this.userInfo.timezone);
			}
		});
	}

	getSubStepKeys = (stepKey) => Object.keys(this.steps[stepKey].subSteps).filter(subKey => this.steps[stepKey].subSteps[subKey].visible !== false);
	getFirstSubStep = (stepKey) => this.getSubStepKeys(stepKey)[0];
	getLastSubStep = (stepKey) => {
		const keys = this.getSubStepKeys(stepKey);
		return keys[keys.length - 1];
	}
	getNextSubStep = () => {
		const keys = this.getSubStepKeys(this.activeStepKey);
		const index = keys.indexOf(this.activeSubStepKey);
		return index < keys.length - 1 ? keys[index + 1] : null;
	}
	getPrevSubStep = () => {
		const keys = this.getSubStepKeys(this.activeStepKey);
		const index = keys.indexOf(this.activeSubStepKey);
		return index > 0 ? keys[index - 1] : null;
	}

	findNextVisibleStep = (stepKey, direction: 'next' | 'prev') => {
		const stepKeys = Object.keys(this.steps);
		const currentIndex = stepKeys.indexOf(stepKey);
		const delta = direction === 'next' ? 1 : -1;
		for (let index = currentIndex + delta; index >= 0 && index < stepKeys.length; index += delta) {
			if (this.steps[stepKeys[index]].visible !== false) {
				return stepKeys[index];
			}
		}
		return null;
	}

	next = () => {
		if (!this.validateStep()) {
			return;
		}

		// CC: confirm architecture selection when leaving the arch substeps
		if (this.activeStepKey === 'emailArchitecture' && this.activeSubStepKey === 'outboundArch' && this.selectedOutboundArchitecture) {
			this.confirmArchitectureChange('outbound');
			return;
		}
		if (this.activeStepKey === 'emailArchitecture' && this.activeSubStepKey === 'inboundArch' && this.selectedInboundArchitecture) {
			this.confirmArchitectureChange('inbound');
			return;
		}

		// CC: save notification options when leaving the substep
		if (this.activeStepKey === 'inboundShield' && this.activeSubStepKey === 'notificationOptions' && this.notificationOptionsComponent) {
			this.notificationOptionsComponent.changeNotifyPeriod();
			return;
		}

		// CC: save look & feel when leaving the branding substep
		if (this.activeStepKey === 'generalSettings' && this.activeSubStepKey === 'branding' && this.lookAndFeelComponent && !this.lookAndFeelComponent.userLogoDiff) {
			this.lookAndFeelComponent.openUpdateLogoDetailsPopup();
			return;
		}

		// CC: apply copied policies when leaving the copyPolicy substep (skip if nothing selected)
		if (this.activeStepKey === 'generalSettings' && this.activeSubStepKey === 'copyPolicy' && this.onboardingCopyPolicyComponent) {
			if (this.onboardingCopyPolicyComponent.checkPolicySelected() && this.onboardingCopyPolicyComponent.selectedFromAdmin) {
				this.onboardingCopyPolicyComponent.applyPolicy();
				return;
			}
		}

		// CC: enable protection for checked mailboxes when leaving the graph deploy step
		if (this.activeStepKey === 'deployInboundShield' && this.activeSubStepKey === 'deploy' && this.onboardingMailboxesComponent) {
			this.onboardingMailboxesComponent.enableCheckedMailboxes();
			return;
		}

		this.proceedToNextStep();
	}

	proceedToNextStep = () => {
		const nextSubStep = this.getNextSubStep();
		if (nextSubStep) {
			this.activeSubStepKey = nextSubStep;
		} else {
			const nextStepKey = this.findNextVisibleStep(this.activeStepKey, 'next');
			if (nextStepKey) {
				this.activeStepKey = nextStepKey;
				this.activeSubStepKey = this.getFirstSubStep(this.activeStepKey);
			} else {
				this.wizardComplete = true;
				return;
			}
		}
		this.updateNavigationState();
	}

	back = () => {
		const prevSubStep = this.getPrevSubStep();
		if (prevSubStep) {
			this.activeSubStepKey = prevSubStep;
		} else {
			const prevStepKey = this.findNextVisibleStep(this.activeStepKey, 'prev');
			if (prevStepKey) {
				this.activeStepKey = prevStepKey;
				this.activeSubStepKey = this.getLastSubStep(this.activeStepKey);
			}
		}
		this.updateNavigationState();
	}

	skip = () => {
		const nextStepKey = this.findNextVisibleStep(this.activeStepKey, 'next');
		if (nextStepKey) {
			this.activeStepKey = nextStepKey;
			this.activeSubStepKey = this.getFirstSubStep(this.activeStepKey);
		} else {
			this.wizardComplete = true;
			return;
		}
		this.updateNavigationState();
	}

	goToStep = (stepKey) => {
		this.activeStepKey = stepKey;
		this.activeSubStepKey = this.getFirstSubStep(stepKey);
		this.updateNavigationState();
	}

	selectEmailService = (serviceKey) => {
		this.steps.environmentScope.selectedEmailService = serviceKey;
		this.updateArchSupport();
		this.updateStepVisibility();
		this.outboundPreviewSpec = buildOutboundPreviewSpec(serviceKey, this.selectedOutboundArchitecture?.key);
		this.inboundPreviewSpec = buildInboundPreviewSpec(serviceKey, this.selectedInboundArchitecture?.key);
	}

	// CC: check if a scope option is allowed based on wizardInfo.permissions
	isScopeAllowed = (scopeKey) => {
		const permissions = this.wizardInfo?.permissions;
		if (!permissions) {
			return true;
		}
		if (scopeKey === 'both') {
			return permissions.inbound && permissions.outbound;
		}
		if (scopeKey === this.dic.CONSTANTS.onboardingSetupScopes.inbound) {
			return permissions.inbound;
		}
		if (scopeKey === this.dic.CONSTANTS.onboardingSetupScopes.outbound) {
			return permissions.outbound;
		}
		return true;
	}

	// CC: auto-select the first allowed scope based on permissions
	applyPermissionsToScope = () => {
		const permissions = this.wizardInfo?.permissions;
		if (!permissions) {
			return;
		}
		this.noScopePermission = !permissions.inbound && !permissions.outbound;
		if (!this.isScopeAllowed(this.steps.environmentScope.selectedScope)) {
			if (permissions.inbound && permissions.outbound) {
				this.selectScope('both');
			} else if (permissions.outbound) {
				this.selectScope(this.dic.CONSTANTS.onboardingSetupScopes.outbound);
			} else if (permissions.inbound) {
				this.selectScope(this.dic.CONSTANTS.onboardingSetupScopes.inbound);
			}
		}
	}

	selectScope = (scopeKey) => {
		if (!this.isScopeAllowed(scopeKey)) {
			return;
		}
		this.steps.environmentScope.selectedScope = scopeKey;
		this.outboundSelected = scopeKey === 'both' || scopeKey === this.dic.CONSTANTS.onboardingSetupScopes.outbound;
		this.inboundSelected = scopeKey === 'both' || scopeKey === this.dic.CONSTANTS.onboardingSetupScopes.inbound;
		this.updateStepVisibility();
	}

	validateStep = () => {
		this.wizardError = '';

		// CC: block proceeding if the selected scope is not allowed by permissions
		if (this.activeStepKey === 'environmentScope' && !this.isScopeAllowed(this.steps.environmentScope.selectedScope)) {
			this.wizardError = 'You do not have permission to edit the selected protection scope';
			return false;
		}

		return true;
	}

	updateStepVisibility = () => {
		const scope = this.steps.environmentScope.selectedScope;
		const outboundVisible = scope === 'both' || scope === this.dic.CONSTANTS.onboardingSetupScopes.outbound;
		const inboundVisible = scope === 'both' || scope === this.dic.CONSTANTS.onboardingSetupScopes.inbound;
		this.steps.emailArchitecture.subSteps.outboundArch.visible = outboundVisible;
		this.steps.emailArchitecture.subSteps.inboundArch.visible = inboundVisible;

		// CC: branding applies to more than the encrypted email template - say so, especially when outbound is not being deployed
		this.steps.generalSettings.subSteps.branding.explanation = outboundVisible ?
			'Customize your encrypted email template, Inbound Shield notifications (quarantine digests, release requests) and the Trustifi web app with your organization\'s logo and brand colors to create a consistent and professional experience.' :
			'Customize Inbound Shield notifications (quarantine digests, release requests) and the Trustifi web app with your organization\'s logo and brand colors. The same branding will also apply to encrypted emails if you deploy Outbound Shield later.';

		// CC: if email env is 'other' → skip mail server API integration
		const isOtherEnv = this.steps.environmentScope.selectedEmailService === this.dic.CONSTANTS.onboardingFormValues.currentEnv.other;
		this.steps.mailServer.visible = !isOtherEnv;

		// CC: if outbound arch is 'journal' → skip outbound configuration
		const outboundIsJournal = outboundVisible && this.confirmedOutboundArch === this.dic.CONSTANTS.sendingArchitecture.journal;
		this.steps.outboundShield.visible = outboundVisible && !outboundIsJournal;
		this.steps.deployOutboundShield.visible = outboundVisible;
		// CC: show verifyMta substep only for 'Your MTA' architecture
		this.steps.deployOutboundShield.subSteps.verifyMta.visible = this.confirmedOutboundArch === this.dic.CONSTANTS.sendingArchitecture.client;

		// CC: skip domain verification if outbound journal (when outbound selected) or inbound API (when inbound selected)
		// CC: show domains if any selected direction still needs them
		const inboundIsApi = inboundVisible && this.confirmedInboundArch === this.dic.CONSTANTS.inboundSendingArchitecture.graph;
		const outboundNeedsDomains = outboundVisible && !outboundIsJournal;
		const inboundNeedsDomains = inboundVisible && !inboundIsApi;
		this.steps.configureDomains.visible = outboundNeedsDomains || inboundNeedsDomains;

		this.steps.inboundShield.visible = inboundVisible;
		this.steps.deployInboundShield.visible = inboundVisible;
		// CC: show verifyMta substep for connector and mx inbound architectures
		this.steps.deployInboundShield.subSteps.verifyMta.visible = this.confirmedInboundArch === this.dic.CONSTANTS.inboundSendingArchitecture.connector || this.confirmedInboundArch === this.dic.CONSTANTS.inboundSendingArchitecture.mx;
		// CC: Selective Mailbox Protection sub-step only for MX - with the connector / journal architectures the scanned mailboxes are
		// chosen in the mail server configuration itself, and API has its own mailbox step (the section stays available in Plan Settings)
		this.steps.deployInboundShield.subSteps.mailboxes.visible = this.confirmedInboundArch === this.dic.CONSTANTS.inboundSendingArchitecture.mx;

		// CC: update deployment guides based on selected environment and architecture
		const env = this.steps.environmentScope.selectedEmailService;
		this.activeOutboundGuides = this.outboundDeploymentGuides[env]?.[this.confirmedOutboundArch] || [];
		this.activeInboundGuides = this.inboundDeploymentGuides[env]?.[this.confirmedInboundArch] || [];

		let num = 1;
		for (const stepKey of Object.keys(this.steps)) {
			if (this.steps[stepKey].visible !== false) {
				this.steps[stepKey].num = num++;
			}
		}
	}

	// CC: the architecture is saved immediately (even if the wizard is never completed), so when the plan already has a live mail flow
	// on a different architecture, ask for confirmation before switching - same as the plan settings pages do
	confirmArchitectureChange = (direction: 'outbound' | 'inbound') => {
		const isOutbound = direction === 'outbound';
		const architectures = isOutbound ? this.outboundArchitectures : this.inboundArchitectures;
		const selectedArch = isOutbound ? this.selectedOutboundArchitecture : this.selectedInboundArchitecture;
		const confirmedArch = isOutbound ? this.confirmedOutboundArch : this.confirmedInboundArch;
		const relay = isOutbound ? this.wizardInfo?.outbound_relay : this.wizardInfo?.inbound_relay;
		const updateArchitecture = isOutbound ? this.updateOutboundArchitecture : this.updateInboundArchitecture;

		if (selectedArch.key === confirmedArch || !relay?.enabled) {
			updateArchitecture(selectedArch.key);
			return;
		}

		this.gs.showPopup({
			title: `Change ${isOutbound ? 'Outbound' : 'Inbound'} Mail Flow Architecture`,
			subTitle: `Please note - you are about to change your ${direction} mail flow architecture from "${architectures[confirmedArch]?.title || confirmedArch}" to "${selectedArch.title}"`,
			body: [
				'The change takes effect immediately, even if you do not complete the wizard.',
				`If your email server is still configured for the current architecture, ${direction} emails may fail or be lost until the new configuration is completed.`
			],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Change',
			doneCb: () => {
				updateArchitecture(selectedArch.key);
			}
		});
	}

	// CC: taken from email-relay-integration.component.ts -> selectArchitecture
	selectOutboundArchitecture = (architectureName) => {
		if (!architectureName || !this.outboundArchitectures[architectureName]) {
			return;
		}
		this.selectedOutboundArchitecture = this.outboundArchitectures[architectureName];
		this.selectedOutboundArchitecture.key = architectureName;
		this.outboundPreviewSpec = buildOutboundPreviewSpec(this.steps.environmentScope.selectedEmailService, architectureName);
	}

	updateOutboundArchitecture = (architectureName) => {
		if (this.updatingArchitecture) {
			return;
		}

		this.updatingArchitecture = true;
		const updateData = {
			action: this.dic.CONSTANTS.erSettingsActions.sendingArchitecture,
			sendingArchitecture: architectureName,
			skipCheck: true,
			isWizardMode: true
		};

		this.rs.activateEmailRelayAndGenKey(updateData).then(() => {
			this.wizardInfo.outbound_relay.sending_architecture = architectureName;
			this.confirmedOutboundArch = architectureName;
			this.selectOutboundArchitecture(architectureName);
			this.updateStepVisibility();
			this.proceedToNextStep();
		}).finally(() => {
			this.updatingArchitecture = false;
		});
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> selectArchitecture
	selectInboundArchitecture = (architectureName) => {
		if (!architectureName || !this.inboundArchitectures[architectureName]) {
			return;
		}
		this.selectedInboundArchitecture = this.inboundArchitectures[architectureName];
		this.selectedInboundArchitecture.key = architectureName;
		this.inboundPreviewSpec = buildInboundPreviewSpec(this.steps.environmentScope.selectedEmailService, architectureName);
	}

	updateInboundArchitecture = (architectureName) => {
		if (this.updatingArchitecture) {
			return;
		}

		this.updatingArchitecture = true;
		const updateData = {
			action: this.dic.CONSTANTS.inboundRelayActions.sendingArchitecture,
			sendingArchitecture: architectureName,
			skipCheck: true,
			isWizardMode: true
		};

		this.rs.activateInboundEmailRelayAndGenKey(updateData).then(() => {
			this.wizardInfo.inbound_relay.sending_architecture = architectureName;
			this.confirmedInboundArch = architectureName;
			this.selectInboundArchitecture(architectureName);
			this.updateStepVisibility();
			this.proceedToNextStep();
		}).finally(() => {
			this.updatingArchitecture = false;
		});
	}

	// CC: taken from region.component.ts -> changeRegion
	changeRegion = (regionName) => {
		if (this.userInfo.plan.use_region === regionName) {
			return;
		}

		this.gs.showPopup({
			title: 'Change Region',
			subTitle: `Please note - your new secure data region will be ${regionName}`,
			body: ['This will not affect secure data which was previously saved in another region.'],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Change',
			doneCb: () => {
				this.rs.changeRegion({region: regionName}).then(() => {
					this.userInfo.plan.use_region = regionName;
					this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
				});
			},
		});
	}

	updateUserTimezone = (timezoneObj) => {
		this.selectedTimeZone = timezoneObj;

		const actionInfo = {
			action: this.dic.CONSTANTS.planUsersActions.updateTimezone,
			timezone: timezoneObj.zone,
			isAdmin: true,
		};

		this.rs.doActionOnPlanUser(this.userInfo.email, actionInfo).then((response) => {
			this.userInfo.timezone = timezoneObj.zone;
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
		});
	}

	// CC: taken from email-relay-integration.component.ts -> updateSendWelcomeSettings
	updateSendWelcomeSettings = (type) => {
		const updateData = {
			action: this.dic.CONSTANTS.erSettingsActions.welcomeEmail,
			type,
			value: this.wizardInfo.outbound_relay.welcome_email[type]
		};

		this.rs.activateEmailRelayAndGenKey(updateData).then(() => {
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
		});
	}

	updateFooterStatus = () => {
		const tpData = {
			type: this.dic.CONSTANTS.rulesTpAction.footer,
			action: 'status',
			enabled: this.wizardInfo.threat_protection.add_footer
		};

		this.rs.updateTpRules('', tpData).then(() => {});
	}

	updateFooterContent = () => {
		if (!this.wizardInfo.threat_protection.add_footer) {
			return;
		}

		const tpData = {
			type: this.dic.CONSTANTS.rulesTpAction.footer,
			action: 'customization',
			customization_footer: this.wizardInfo.threat_protection.customization_footer,
		};

		this.rs.updateTpRules('', tpData).then(() => {});
	}

	// CC: taken from inbound-configuration.componenet.ts -> changeOnClickScanStatus
	changeOnClickScanStatus = () => {
		let body, subTitle;
		if (this.wizardInfo.threat_protection.url_rewrite.enabled) {
			subTitle = 'You are about to enable On-click link scan';
			body = [
				'Links in received emails will be actively scanned for threats when clicked by users.',
				'If you are using any other URL re-writing services, for example Microsoft SafeLinks, it is recommended to disable this service before using Trustifi\'s on-click link scan'
			];
		}
		else {
			subTitle = 'You are about to disable On-click link scan';
			body = ['Links in received emails will no longer be scanned when clicked by users.'];
		}

		this.gs.showPopup({
			title: 'On-click Link Scan',
			subTitle: subTitle,
			body: body,
			type: this.dic.CONSTANTS.popupInfo,
			doneBtnText: 'Confirm',
			doneCb: () => {
				this.updateUrlRewriteSettings(this.dic.CONSTANTS.inboundUrlRewriteActions.enabled);
			},
			cancelCb: () => {
				this.wizardInfo.threat_protection.url_rewrite.enabled = !this.wizardInfo.threat_protection.url_rewrite.enabled;
			}
		});
	}

	// CC: taken from inbound-configuration.componenet.ts -> updateUrlRewriteSettings
	updateUrlRewriteSettings = (action) => {
		if (this.updateUrlRewriteSettingsInProcess) {
			return;
		}

		const tpData: any = {
			action: action,
			type: this.dic.CONSTANTS.rulesTpAction.urlRewrite
		};

		switch (action) {
			case this.dic.CONSTANTS.inboundUrlRewriteActions.enabled:
				tpData.data = {enabled: this.wizardInfo.threat_protection.url_rewrite.enabled};
				break;

			case this.dic.CONSTANTS.inboundUrlRewriteActions.allowMaliciousOnClick:
				tpData.data = {allow_malicious_onclick: this.wizardInfo.threat_protection.url_rewrite.allow_malicious_onclick};
				break;

			case this.dic.CONSTANTS.inboundUrlRewriteActions.urlHunting:
				tpData.data = {url_tracking: this.wizardInfo.threat_protection.url_rewrite.url_tracking};
				break;
		}

		this.updateUrlRewriteSettingsInProcess = true;

		this.rs.updateTpRules('', tpData).then(() => {
			this.updateUrlRewriteSettingsInProcess = false;
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
		}, () => {
			this.updateUrlRewriteSettingsInProcess = false;
		});
	}


	// CC: taken from email-relay-integration.component.ts -> refreshOutboundRelay helper
	refreshOutboundRelay = (response) => {
		this.wizardInfo.outbound_relay = response || {};
		if (this.wizardInfo.outbound_relay) {
			this.wizardInfo.outbound_relay.keyAndSecret = `${this.wizardInfo.outbound_relay.key}:${this.wizardInfo.outbound_relay.secret}`;
		}
	}

	// CC: taken from email-relay-integration.component.ts -> regenerateEmailRelayKey
	regenerateEmailRelayKey = (keyNumber) => {
		this.gs.showPopup({
			title: 'Regenerate Email Relay Key',
			subTitle: 'You are about to change your Email Relay key!',
			body: ['All users under Email-Server that configured this key will not be able to send emails through Email Relay.'],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Regenerate',
			doneCb: () => {
				const actionInfo = {
					action: this.dic.CONSTANTS.erSettingsActions.emailRelay,
					type: 'keys',
					keyAction: 'regenerate',
					key: keyNumber
				};
				this.regenerateInProcessER = true;
				this.rs.activateEmailRelayAndGenKey(actionInfo).then((response) => {
					this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
					this.refreshOutboundRelay(response);
					this.regenerateInProcessER = false;
				}, () => {
					this.regenerateInProcessER = false;
				});
			}
		});
	}



	// CC: taken from email-relay-integration.component.ts -> onDragStartMta
	onDragStartMta = (mta) => {
		this.mtaToMove = mta;
	}

	// CC: taken from email-relay-integration.component.ts -> onDropMta
	onDropMta = (event: CdkDragDrop<any[]>) => {
		if (!this.mtaToMove) {
			return;
		}
		const prevIndex = this.wizardInfo.outbound_relay.mtas.findIndex(mtaItem => mtaItem === this.mtaToMove);
		const currIndex = event.currentIndex;
		moveItemInArray(this.wizardInfo.outbound_relay.mtas, prevIndex, currIndex);
		this.updateEmailRelayConfig(this.dic.CONSTANTS.erSettingsActions.mta, 'move', currIndex);
	}

	// CC: taken from email-relay-integration.component.ts -> resolveDomainHost
	resolveDomainHost = (objToUpdate, domain) => {
		if (!this.gs.isValidDomain(domain)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidDomain, domain));
			this.outboundRelayDomainErr = true;
			return;
		}
		this.updateMtaInProcess = true;
		const updateData = {
			action: this.dic.CONSTANTS.erSettingsActions.domainMta,
			domain: domain
		};
		this.rs.activateEmailRelayAndGenKey(updateData).then((response) => {
			objToUpdate.host = response.exchange;
			this.updateMtaInProcess = false;
		}, () => {
			this.updateMtaInProcess = false;
		});
	}

	// CC: taken from email-relay-integration.component.ts -> addMta
	addMta = () => {
		if (!this.gs.isValidDomain(this.wizardInfo.outbound_relay.domain)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidDomain, this.wizardInfo.outbound_relay.domain));
			this.outboundRelayDomainErr = true;
			return;
		}
		this.wizardInfo.outbound_relay.newPort = parseInt(this.wizardInfo.outbound_relay.newPort);
		const updateData = {
			action: this.dic.CONSTANTS.erSettingsActions.mta,
			type: 'add',
			host: this.wizardInfo.outbound_relay.host,
			port: this.wizardInfo.outbound_relay.newPort,
			domain: this.wizardInfo.outbound_relay.domain
		};
		this.updateMtaInProcess = true;
		this.rs.activateEmailRelayAndGenKey(updateData).then((response) => {
			this.updateMtaInProcess = false;
			this.wizardInfo.outbound_relay.mtas = response.mtas;
			this.wizardInfo.outbound_relay.host = '';
			this.wizardInfo.outbound_relay.newPort = '';
			this.wizardInfo.outbound_relay.domain = '';
			this.verifyMta(this.wizardInfo.outbound_relay.mtas[this.wizardInfo.outbound_relay.mtas.length - 1]);
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
		}, () => {
			this.updateMtaInProcess = false;
		});
	}

	// CC: taken from email-relay-integration.component.ts -> updateEmailRelayConfig
	updateEmailRelayConfig = (action, type = '', index = null) => {
		this.updateMtaInProcess = true;
		let updateData: any = {action};
		switch (action) {
			case this.dic.CONSTANTS.erSettingsActions.mta:
				if (type === 'move') {
					updateData._id = this.mtaToMove._id;
					updateData.idx = index;
				}
				else if (type === 'edit') {
					updateData._id = this.mtaInEdit._id;
					updateData.host = this.mtaInEdit.host;
					updateData.port = parseInt(this.mtaInEdit.port);
					updateData.domain = this.mtaInEdit.domain;
				}
				else {
					updateData._id = this.wizardInfo.outbound_relay.mtas[index]._id;
					updateData.domain = this.wizardInfo.outbound_relay.mtas[index].domain;
				}
				updateData.type = type;
				break;
		}
		this.rs.activateEmailRelayAndGenKey(updateData).then((response) => {
			this.updateMtaInProcess = false;
			switch (action) {
				case this.dic.CONSTANTS.erSettingsActions.mta:
					for (let idx = 0; idx < response.mtas.length; idx++) {
						if (this.wizardInfo.outbound_relay.mtas[idx]?.editMode) {
							response.mtas[idx].editMode = true;
							this.mtaInEdit = response.mtas[idx];
						}
					}
					this.wizardInfo.outbound_relay.mtas = response.mtas;
					if (type === 'move') {
						this.mtaToMove = null;
					}
					else if (type === 'edit') {
						this.verifyMta(this.mtaInEdit);
						this.mtaInEdit.editMode = false;
						this.mtaInEdit = null;
					}
					this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
					break;
			}
		}, () => {
			this.updateMtaInProcess = false;
		});
	}

	// CC: taken from email-relay-integration.component.ts -> setMTAForEdit
	setMTAForEdit = (mta) => {
		this.wizardInfo.outbound_relay.mtas.forEach(mtaItem => {
			mtaItem.editMode = false;
		});
		mta.editMode = true;
		this.mtaInEdit = mta;
	}

	// CC: taken from email-relay-integration.component.ts -> verifyMta
	verifyMta = (mtaObj) => {
		if (this.updateMtaInProcess) {
			return;
		}
		this.updateMtaInProcess = true;
		const actionInfo = {
			action: this.dic.CONSTANTS.inboundRelayActions.mta,
			type: 'verify',
			host: mtaObj.host,
			port: mtaObj.port,
		};
		mtaObj.loading = true;
		this.rs.activateEmailRelayAndGenKey(actionInfo).then((response) => {
			mtaObj.verified = response.meta?.type === 'info';
			mtaObj.loading = false;
			this.updateMtaInProcess = false;
		}, () => {
			this.updateMtaInProcess = false;
			mtaObj.loading = false;
			mtaObj.verified = false;
		});
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> onDragStartMta
	onDragStartInboundMta = (mta) => {
		this.inboundMtaToMove = mta;
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> onDropMta
	onDropInboundMta = (event: CdkDragDrop<any[]>) => {
		if (!this.inboundMtaToMove) {
			return;
		}
		const prevIndex = this.wizardInfo.inbound_relay.mtas.findIndex(mtaItem => mtaItem === this.inboundMtaToMove);
		const currIndex = event.currentIndex;
		moveItemInArray(this.wizardInfo.inbound_relay.mtas, prevIndex, currIndex);
		this.updateInboundEmailRelayConfig(this.dic.CONSTANTS.erSettingsActions.mta, 'move', currIndex);
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> updateEmailRelayConfig (domainMta action)
	resolveInboundDomainHost = () => {
		if (!this.gs.isValidDomain(this.wizardInfo.inbound_relay.domain)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidDomain, this.wizardInfo.inbound_relay.domain));
			this.inboundRelayDomainErr = true;
			return;
		}
		this.updateInboundMtaInProcess = true;
		const updateData = {
			action: this.dic.CONSTANTS.inboundRelayActions.domainMta,
			domain: this.wizardInfo.inbound_relay.domain
		};
		this.rs.activateInboundEmailRelayAndGenKey(updateData).then((response) => {
			this.wizardInfo.inbound_relay.host = response.exchange;
			this.updateInboundMtaInProcess = false;
		}, () => {
			this.updateInboundMtaInProcess = false;
		});
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> addMta
	addInboundMta = () => {
		if (!this.gs.isValidDomain(this.wizardInfo.inbound_relay.domain)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidDomain, this.wizardInfo.inbound_relay.domain));
			this.inboundRelayDomainErr = true;
			return;
		}
		this.wizardInfo.inbound_relay.newPort = parseInt(this.wizardInfo.inbound_relay.newPort);
		const updateData = {
			action: this.dic.CONSTANTS.erSettingsActions.mta,
			type: 'add',
			host: this.wizardInfo.inbound_relay.host,
			port: this.wizardInfo.inbound_relay.newPort,
			domain: this.wizardInfo.inbound_relay.domain
		};
		this.updateInboundMtaInProcess = true;
		this.rs.activateInboundEmailRelayAndGenKey(updateData).then((response) => {
			this.updateInboundMtaInProcess = false;
			this.wizardInfo.inbound_relay.mtas = response.mtas;
			this.wizardInfo.inbound_relay.host = '';
			this.wizardInfo.inbound_relay.newPort = '';
			this.wizardInfo.inbound_relay.domain = '';
			this.verifyInboundMta(this.wizardInfo.inbound_relay.mtas[this.wizardInfo.inbound_relay.mtas.length - 1]);
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
		}, () => {
			this.updateInboundMtaInProcess = false;
		});
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> updateEmailRelayConfig
	updateInboundEmailRelayConfig = (action, type = '', index = null) => {
		this.updateInboundMtaInProcess = true;
		let updateData: any = {action};
		switch (action) {
			case this.dic.CONSTANTS.erSettingsActions.mta:
				if (type === 'move') {
					updateData._id = this.inboundMtaToMove._id;
					updateData.idx = index;
				}
				else if (type === 'edit') {
					updateData._id = this.inboundMtaInEdit._id;
					updateData.host = this.inboundMtaInEdit.host;
					updateData.port = parseInt(this.inboundMtaInEdit.port);
					updateData.domain = this.inboundMtaInEdit.domain;
				}
				else {
					updateData._id = this.wizardInfo.inbound_relay.mtas[index]._id;
					updateData.domain = this.wizardInfo.inbound_relay.mtas[index].domain;
				}
				updateData.type = type;
				break;
		}
		this.rs.activateInboundEmailRelayAndGenKey(updateData).then((response) => {
			this.updateInboundMtaInProcess = false;
			switch (action) {
				case this.dic.CONSTANTS.erSettingsActions.mta:
					for (let idx = 0; idx < response.mtas.length; idx++) {
						if (this.wizardInfo.inbound_relay.mtas[idx]?.editMode) {
							response.mtas[idx].editMode = true;
							this.inboundMtaInEdit = response.mtas[idx];
						}
					}
					this.wizardInfo.inbound_relay.mtas = response.mtas;
					if (type === 'move') {
						this.inboundMtaToMove = null;
					}
					else if (type === 'edit') {
						this.verifyInboundMta(this.inboundMtaInEdit);
						this.inboundMtaInEdit.editMode = false;
						this.inboundMtaInEdit = null;
					}
					this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
					break;
			}
		}, () => {
			this.updateInboundMtaInProcess = false;
		});
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> setMTAForEdit
	setInboundMTAForEdit = (mta) => {
		this.wizardInfo.inbound_relay.mtas.forEach(mtaItem => {
			mtaItem.editMode = false;
		});
		mta.editMode = true;
		this.inboundMtaInEdit = mta;
	}

	// CC: taken from inbound-email-relay-integration.component.ts -> verifyMta
	verifyInboundMta = (mtaObj) => {
		if (this.updateInboundMtaInProcess) {
			return;
		}
		this.updateInboundMtaInProcess = true;
		const actionInfo = {
			action: this.dic.CONSTANTS.inboundRelayActions.mta,
			type: 'verify',
			host: mtaObj.host,
			port: mtaObj.port,
		};
		mtaObj.loading = true;
		this.rs.activateInboundEmailRelayAndGenKey(actionInfo).then((response) => {
			mtaObj.verified = response.meta?.type === 'info';
			mtaObj.loading = false;
			this.updateInboundMtaInProcess = false;
		}, () => {
			this.updateInboundMtaInProcess = false;
			mtaObj.loading = false;
			mtaObj.verified = false;
		});
	}

	updateNavigationState = () => {
		this.isFirstPosition = this.activeStepKey === 'environmentScope' &&
			this.activeSubStepKey === this.getFirstSubStep('environmentScope');

		this.hasNextPosition = !!this.getNextSubStep() || !!this.findNextVisibleStep(this.activeStepKey, 'next');

		if (this.verifyPollingInterval && this.activeSubStepKey !== 'verify') {
			this.stopVerifyPolling();
		}

		switch (this.activeStepKey) {
			case 'emailArchitecture':
				switch (this.activeSubStepKey) {
					case 'outboundArch':
						// CC: default to first available architecture if none selected
						if (!this.selectedOutboundArchitecture || !this.activeOutboundArchSupport[this.selectedOutboundArchitecture.key]) {
							const firstAvailableKey = Object.keys(this.outboundArchitectures).find(key => this.activeOutboundArchSupport[key]);
							if (firstAvailableKey) {
								this.selectOutboundArchitecture(firstAvailableKey);
							}
						}
						break;
					case 'inboundArch':
						// CC: default to first available architecture if none selected
						if (!this.selectedInboundArchitecture || !this.activeInboundArchSupport[this.selectedInboundArchitecture.key]) {
							const firstAvailableKey = Object.keys(this.inboundArchitectures).find(key => this.activeInboundArchSupport[key]);
							if (firstAvailableKey) {
								this.selectInboundArchitecture(firstAvailableKey);
							}
						}
						break;
				}
				break;

			case 'inboundShield':
				switch (this.activeSubStepKey) {
					case 'notificationOptions':
						// CC: fetch mainRulePreset for notification-options-component
						// TODO: we we can use the wizardInfo
						this.rs.getTPRules().then((response) => {
							this.mainRulePreset = {
								name: this.dic.CONSTANTS.lfMainCustomizationName,
								...response.config.threat_protection.rules
							};
						});
						break;

					case 'additionalSettings':
						// CC: re-fetch the threat protection config - the wizard info does not include the url_rewrite settings
						this.rs.getTPRules().then((response) => {
							this.wizardInfo.threat_protection = response.config.threat_protection;
						});
						break;
				}
				break;

			case 'deployOutboundShield':
				switch (this.activeSubStepKey) {
					case 'verifyMta':
						// CC: re-fetch outbound relay data since MTAs may have changed
						this.rs.getOutboundPlanSettings().then((planInfo) => {
							this.wizardInfo.outbound_relay = planInfo.outbound_relay;
						});
						break;

					case 'deploy':
						this.rs.getOutboundPlanSettings().then((planInfo) => {
							this.wizardInfo.outbound_relay = planInfo.outbound_relay;
							this.wizardInfo.outbound_relay.journalEmailAddress = planInfo.journalEmailAddress;
							this.wizardInfo.outbound_relay.keyAndSecret = `${planInfo.outbound_relay.key}:${planInfo.outbound_relay.secret}`;
						});

						this.isPsAvailable = this.steps.environmentScope.selectedEmailService !== this.dic.CONSTANTS.onboardingFormValues.currentEnv.gSuite
							&& this.confirmedOutboundArch !== this.dic.CONSTANTS.sendingArchitecture.journal &&
							(this.steps.environmentScope.selectedEmailService == this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud ||
								this.steps.environmentScope.selectedEmailService == this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid);
						this.outboundDeployMode = this.isPsAvailable ? 'automatic' : 'manual';
						break;

					case 'verify':
						this.startVerifyPolling();
						break;
				}
				break;

			case 'deployInboundShield':
				switch (this.activeSubStepKey) {
					case 'verifyMta':
					case 'mailboxes':
						// CC: re-fetch inbound relay data since MTAs / protected mailboxes may have changed
						this.rs.getInboundPlanSettings().then((planInfo) => {
							this.wizardInfo.inbound_relay = planInfo.inbound_relay;
						});
						break;

					case 'deploy':
						this.rs.getInboundPlanSettings().then((planInfo) => {
							this.wizardInfo.inbound_relay = planInfo.inbound_relay;
							if (planInfo.inbound_relay.key && planInfo.inbound_relay.secret) {
								this.wizardInfo.inbound_relay.keyAndSecret = `${planInfo.inbound_relay.key}:${planInfo.inbound_relay.secret}`;
							}
							this.wizardInfo.inbound_relay.journalEmailAddress = planInfo.journalEmailAddress;
							this.wizardInfo.inbound_relay.mx = planInfo.mx;
							// CC: static_ip_set_ips only exist on the plan settings response, not on the initial wizard info
							this.inboundGoogleIps = this.gs.getGoogleWorkspaceIpFormats(planInfo.inbound_relay.static_ip_set_ips);
						});

						this.isPsAvailable = this.confirmedInboundArch === this.dic.CONSTANTS.inboundSendingArchitecture.connector &&
							(this.steps.environmentScope.selectedEmailService == this.dic.CONSTANTS.onboardingFormValues.currentEnv.exchangeCloud ||
								this.steps.environmentScope.selectedEmailService == this.dic.CONSTANTS.onboardingFormValues.currentEnv.hybrid);
						this.inboundDeployMode = this.isPsAvailable ? 'automatic' : 'manual';
						break;

					case 'verify':
						this.startVerifyPolling();
						break;
				}
				break;
		}
	}

	ngOnDestroy() {
		this.stopVerifyPolling();
	}

	startVerifyPolling = () => {
		if (this.verifyPollingInterval) {
			this.stopVerifyPolling();
		}

		this.verifyTraceResults = null;
		this.verifyTraceInProcess = true;
		this.fetchVerifyTrace();
		this.verifyPollingInterval = setInterval(() => {
			this.fetchVerifyTrace();
		}, 5000);
	}

	stopVerifyPolling = () => {
		if (this.verifyPollingInterval) {
			clearInterval(this.verifyPollingInterval);
			this.verifyPollingInterval = null;
		}
	}

	// CC: taken from email-trace.component.ts -> getEmailTracing
	fetchVerifyTrace = () => {
		const direction = (this.activeStepKey === 'deployOutboundShield') ? 'outbound' : 'inbound';
		const queryData = {
			period: this.dic.CONSTANTS.trendsPeriod.lastHalfHour.value,
			direction: direction,
		};

		this.rs.getEmailTracingLog(queryData).then((data) => {
			const results = data?.logs?.map(item => {
				return {
					...item._source,
					_id: item._id
				};
			}) || [];

			if (results.length) {
				const lastEmail = results[0];
				this.verifyTraceResults = [lastEmail];
				if (lastEmail.status === 'Completed') {
					this.stopVerifyPolling();
					this.verifyTraceInProcess = false;
				}
			}
		}, () => {
			this.verifyTraceInProcess = false;
		});
	}
}
